import { useState, useEffect } from 'react';
import {
    Users,
    UserPlus,
    Shield,
    Building2,
    Check,
    X,
    Trash2,
    Loader2,
    Mail,
    Link,
    AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
    getAllUsers,
    subscribeToUsers,
    updateUserRole,
    toggleUserActive,
    linkUserToContractor,
    AppUser,
    UserRole
} from '../services/users';
import { getContractors, SEED_CONTRACTORS } from '../services/contractors';
import { Contractor } from '../types';
import ProvisionUserModal from './ProvisionUserModal';

export default function UserManagement() {
    const { user, isSuperAdmin, appUser } = useAuth();
    const [users, setUsers] = useState<AppUser[]>([]);
    const [contractors, setContractors] = useState<Contractor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingUser, setEditingUser] = useState<string | null>(null);
    // Split state: One for filtering the LIST, one for EDITING a user
    const [filterContractor, setFilterContractor] = useState<string>('');
    const [assignContractor, setAssignContractor] = useState<string>('');
    const [showProvisionModal, setShowProvisionModal] = useState(false);
    // Seed logic removed as requested


    useEffect(() => {
        if (!appUser) return;

        let unsubscribeUsers: () => void = () => { };

        const loadData = async () => {
            setLoading(true);
            try {
                let contractorsData: Contractor[] = [];

                if (isSuperAdmin) {
                    // Subscribe to real-time users
                    unsubscribeUsers = subscribeToUsers((updatedUsers) => {
                        setUsers(updatedUsers);
                        setLoading(false); // Stop loading after first real-time update
                    });

                    // Still fetch contractors once for now (could be real-time too later)
                    contractorsData = await getContractors();
                } else if (appUser?.role === 'admin' && appUser?.contractorId) {
                    // Get all users, but filter client-side for security (better would be server-side query)
                    unsubscribeUsers = subscribeToUsers((updatedUsers) => {
                        // Filter: Show ONLY users in my team
                        const myTeam = updatedUsers.filter(u => u.contractorId === appUser.contractorId);
                        setUsers(myTeam);
                        setLoading(false);
                    });

                    const allContractors = await getContractors();
                    contractorsData = allContractors.filter(c => c.id === appUser.contractorId);
                } else if (appUser?.contractorId) {
                    // Standard User - likely shouldn't see this page, but safe fallback
                    const allUsers = await getAllUsers();
                    const filteredUsers = allUsers.filter(u => u.contractorId === appUser.contractorId);
                    setUsers(filteredUsers);

                    const allContractors = await getContractors();
                    contractorsData = allContractors.filter(c => c.id === appUser.contractorId);
                    setLoading(false);
                }

                setContractors(contractorsData.length > 0 ? contractorsData : SEED_CONTRACTORS);

                // Only set loading false here if NOT subscribing (because subscription does it on first callback)
                if (!isSuperAdmin) {
                    setLoading(false);
                }

            } catch (err: any) {
                console.error('Error loading data:', err);
                setError(err.message);
                setContractors(SEED_CONTRACTORS);
                setLoading(false);
            }
        };

        loadData();

        return () => {
            if (unsubscribeUsers) unsubscribeUsers();
        };
    }, [appUser, isSuperAdmin]);

    const handleRoleChange = async (uid: string, newRole: UserRole) => {
        try {
            await updateUserRole(uid, newRole);
            setUsers(users.map(u => u.uid === uid ? { ...u, role: newRole } : u));
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleToggleActive = async (uid: string, isActive: boolean) => {
        try {
            await toggleUserActive(uid, isActive);
            setUsers(users.map(u => u.uid === uid ? { ...u, isActive } : u));
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleLinkContractor = async (uid: string) => {
        if (!assignContractor) return;
        try {
            await linkUserToContractor(uid, assignContractor);
            setUsers(users.map(u => u.uid === uid ? { ...u, contractorId: assignContractor, role: 'admin' } : u));
            setEditingUser(null);
            setAssignContractor('');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getContractorName = (contractorId?: string) => {
        if (!contractorId) return '-';
        const contractor = contractors.find(c => c.id === contractorId);
        return contractor?.displayName || contractorId;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8 items-start sm:items-center justify-between bg-gradient-to-r from-blue-900/10 via-transparent to-transparent p-6 rounded-2xl border border-white/5">
                <div>
                    <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                        <Users className="w-6 h-6 text-blue-400" />
                        Team & Access Control
                    </h2>
                    <p className="text-gray-400 text-sm">Provision new users and manage organization-wide permissions.</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowProvisionModal(true)}
                        className="btn btn-primary shadow-lg shadow-blue-500/20"
                    >
                        <UserPlus className="w-4 h-4" />
                        Provision Elite User
                    </button>
                </div>
            </div>
            {/* Error Message */}
            {error && (
                <div className="card p-4 border-red-500/30 bg-red-500/5">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <p className="text-sm text-red-400">{error}</p>
                        <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-primary">{users.length}</p>
                            <p className="text-xs text-tertiary">Total Users</p>
                        </div>
                    </div>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-primary">
                                {users.filter(u => u.role === 'admin').length}
                            </p>
                            <p className="text-xs text-tertiary">Admins</p>
                        </div>
                    </div>
                </div>
                <div className="card p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-primary">
                                {users.filter(u => u.role === 'user').length}
                            </p>
                            <p className="text-xs text-tertiary">Standard Users</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters (Super Admin Only) */}
            {isSuperAdmin && (
                <div className="flex items-center gap-4 py-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-secondary">Filter by Team:</span>
                        <select
                            value={filterContractor}
                            onChange={(e) => setFilterContractor(e.target.value)}
                            className="input-field py-1.5 px-3 text-sm min-w-[200px]"
                        >
                            <option value="">All Teams</option>
                            {contractors.map(c => (
                                <option key={c.id} value={c.id}>{c.displayName}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )
            }
            {/* Pending Access Requests (Super Admin Only) */}
            {isSuperAdmin && users.some(u => !u.contractorId) && (
                <div className="card border-amber-500/20 bg-amber-500/[0.02] mb-8 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 backdrop-blur-sm">
                    <div className="p-5 border-b border-amber-500/10 bg-amber-500/5 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-amber-500 flex items-center gap-2.5 tracking-wide uppercase">
                            <AlertCircle className="w-5 h-5" />
                            Security Clearance Pending
                        </h3>
                        <span className="text-[10px] font-black bg-amber-500 text-black px-2.5 py-1 rounded flex items-center gap-1.5 animate-pulse">
                            <span className="w-1.5 h-1.5 rounded-full bg-black"></span>
                            {users.filter(u => !u.contractorId).length} REQUESTS
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="text-[10px] font-black text-amber-500/50 uppercase tracking-[0.2em] border-b border-amber-500/10">
                                <tr>
                                    <th className="px-6 py-4">Identity</th>
                                    <th className="px-6 py-4">Registration</th>
                                    <th className="px-6 py-4">Assignment & Clearance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.filter(u => !u.contractorId).map(pendingUser => (
                                    <tr key={pendingUser.uid} className="hover:bg-amber-500/5 transition-all group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 text-sm font-black uppercase group-hover:scale-110 transition-transform">
                                                    {pendingUser.email.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-100">
                                                        {pendingUser.displayName || pendingUser.email.split('@')[0]}
                                                    </p>
                                                    <p className="text-xs text-amber-500/40 font-mono tracking-tighter">{pendingUser.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500 text-xs font-mono">
                                            {formatDate(pendingUser.createdAt || new Date().toISOString())}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <select
                                                    value={editingUser === pendingUser.uid ? assignContractor : ''}
                                                    onChange={(e) => {
                                                        setEditingUser(pendingUser.uid);
                                                        setAssignContractor(e.target.value);
                                                    }}
                                                    className="bg-black/60 border border-amber-500/20 text-gray-200 text-xs rounded-lg px-3 py-2 focus:border-amber-500/50 outline-none w-56 transition-all"
                                                >
                                                    <option value="">Grant Access to Team...</option>
                                                    {contractors.map(c => (
                                                        <option key={c.id} value={c.id}>{c.displayName}</option>
                                                    ))}
                                                </select>
                                                {editingUser === pendingUser.uid && assignContractor && (
                                                    <button
                                                        onClick={() => handleLinkContractor(pendingUser.uid)}
                                                        className="p-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg transition-all shadow-lg shadow-amber-900/50 flex items-center gap-2 font-bold text-xs"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        CLEAR
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="card overflow-hidden border-white/5 bg-secondary/20 backdrop-blur-md">
                <div className="p-5 border-b border-white/5 flex items-center justify-between">
                    <h3 className="font-bold text-gray-200 flex items-center gap-2">
                        Authorized Personnel
                        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Global Registry</span>
                    </h3>
                </div>

                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left text-sm">
                        <thead className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] bg-white/[0.02] border-b border-white/5">
                            <tr>
                                <th className="px-6 py-4">Identity</th>
                                <th className="px-6 py-4">Role / Protocol</th>
                                <th className="px-6 py-4">Deployment Team</th>
                                <th className="px-6 py-4">Operational Status</th>
                                <th className="px-6 py-4">Last Sync</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users
                                .filter(u => !filterContractor || u.contractorId === filterContractor)
                                .length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-20 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-600">
                                            <Shield className="w-8 h-8 opacity-20" />
                                            <p className="text-sm italic">No records found matching current isolation criteria.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                users
                                    .filter(u => !filterContractor || u.contractorId === filterContractor)
                                    .map((item) => (
                                        <tr key={item.uid} className="border-b border-white/[0.02] hover:bg-white/[0.03] transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-white/10 flex items-center justify-center text-gray-400 text-sm font-black uppercase group-hover:border-blue-500/50 transition-all">
                                                        {item.email.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-100 group-hover:text-blue-400 transition-colors">
                                                            {item.displayName || item.email.split('@')[0]}
                                                        </p>
                                                        <p className="text-[10px] text-gray-500 font-mono tracking-tighter uppercase">{item.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={item.role}
                                                    onChange={(e) => handleRoleChange(item.uid, e.target.value as UserRole)}
                                                    disabled={item.uid === user?.uid || (!isSuperAdmin && item.role === 'super_admin')}
                                                    className="bg-black/40 border border-white/10 text-gray-300 text-xs rounded-lg px-2.5 py-1.5 focus:border-blue-500/50 outline-none w-32 font-bold uppercase tracking-wider"
                                                >
                                                    <option value="user">User</option>
                                                    <option value="admin">Admin</option>
                                                    {isSuperAdmin && <option value="super_admin">Super</option>}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4">
                                                {editingUser === item.uid ? (
                                                    <div className="flex items-center gap-2">
                                                        <select
                                                            value={assignContractor}
                                                            onChange={(e) => setAssignContractor(e.target.value)}
                                                            className="bg-black/40 border border-white/10 text-gray-300 text-xs rounded-lg px-2.5 py-1.5 focus:border-blue-500/50 outline-none w-40"
                                                        >
                                                            <option value="">Select Team...</option>
                                                            {contractors.map(c => (
                                                                <option key={c.id} value={c.id}>{c.displayName}</option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            onClick={() => handleLinkContractor(item.uid)}
                                                            className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded-lg"
                                                            title="Confirm"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingUser(null);
                                                            }}
                                                            className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg"
                                                            title="Cancel"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2.5 group/link">
                                                        <span className="text-gray-400 font-medium">
                                                            {getContractorName(item.contractorId)}
                                                        </span>
                                                        {(isSuperAdmin || item.role === 'admin') && (
                                                            <button
                                                                onClick={() => {
                                                                    setEditingUser(item.uid);
                                                                    setAssignContractor(item.contractorId || '');
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-600 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                                                                title="Transfer Team"
                                                            >
                                                                <Link className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleToggleActive(item.uid, !item.isActive)}
                                                    disabled={item.uid === user?.uid}
                                                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${item.isActive
                                                        ? 'bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20'
                                                        : 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20'}`}
                                                >
                                                    {item.isActive ? 'Active' : 'Offline'}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500 text-xs font-mono">
                                                {formatDate(item.lastLogin)}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        className="p-2 text-gray-600 hover:text-blue-400 hover:bg-blue-500/10 rounded-xl transition-all"
                                                        title="Direct Sync"
                                                    >
                                                        <Mail className="w-4 h-4" />
                                                    </button>
                                                    {item.uid !== user?.uid && (
                                                        <button
                                                            className="p-2 text-gray-600 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all"
                                                            title="Revoke Permission"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Help Text */}
            <div className="card p-4 bg-blue-500/5 border-blue-500/20">
                <h4 className="font-medium text-primary mb-2">Role Permissions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-blue-400 font-medium mb-1">Admin</p>
                        <ul className="text-secondary text-xs space-y-0.5">
                            <li>• View all contractor profiles</li>
                            <li>• Manage user accounts</li>
                            <li>• Access analytics</li>
                            <li>• Configure system settings</li>
                        </ul>
                    </div>
                    <div>
                        <p className="text-green-400 font-medium mb-1">Contractor</p>
                        <ul className="text-secondary text-xs space-y-0.5">
                            <li>• View only their linked profile</li>
                            <li>• Search contracts</li>
                            <li>• Save and manage searches</li>
                            <li>• Update their own info</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Invite Modal */}
            {/* Provision User Modal */}
            {showProvisionModal && (
                <ProvisionUserModal
                    onClose={() => setShowProvisionModal(false)}
                    onSuccess={() => {
                        // Real-time listener handles UI update
                        setShowProvisionModal(false);
                    }}
                    // If not super admin, auto-assign to my team
                    prefillContractorId={!isSuperAdmin ? appUser?.contractorId : undefined}
                />
            )}
        </div >
    );
}
